import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, Pencil, Check, X, Trash2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import DeleteConfirmDialog from './DeleteConfirmDialog'

interface SectionCardProps {
  title: string
  onTitleChange?: (title: string) => void
  onDelete?: () => void
  delay?: number
  children: React.ReactNode
  defaultExpanded?: boolean
  hideDelete?: boolean
  hideEditTitle?: boolean
}

const SectionCard = ({
  title,
  onTitleChange,
  onDelete,
  delay = 0,
  children,
  defaultExpanded = true,
  hideDelete = false,
  hideEditTitle = false,
}: SectionCardProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedTitle, setEditedTitle] = useState(title)
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleSaveTitle = () => {
    if (editedTitle.trim()) {
      onTitleChange?.(editedTitle.trim())
    } else {
      setEditedTitle(title)
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditedTitle(title)
    setIsEditing(false)
  }

  return (
    <>
      <motion.div
        className="bg-white rounded-2xl shadow-xs border border-gray-100 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay }}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between gap-3">
            {/* Title Section */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {isEditing && !hideEditTitle ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="h-8 text-sm bg-gray-50"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle()
                      if (e.key === 'Escape') handleCancelEdit()
                    }}
                  />
                  <button
                    onClick={handleSaveTitle}
                    className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <h2
                    className="text-base text-gray-900 truncate uppercase"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}
                  >
                    {title}
                  </h2>
                  {!hideEditTitle && onTitleChange && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                      title="Rename section"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Delete Button */}
              {!hideDelete && onDelete && (
                <button
                  onClick={() => setShowDeleteDialog(true)}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete section"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}

              {/* Expand/Collapse */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-3 space-y-3">{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={onDelete || (() => {})}
        title="Delete Section"
        description="This will remove the section and all its items."
        itemName={title}
      />
    </>
  )
}

export default SectionCard
